"use client";

import { Shield, Star } from "lucide-react";
import type { CarrierComparisonCardData } from "../../types/insurabot-cards";

const S = "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px";

const TIER_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  preferred: { text: "#059669", bg: "#ecfdf5", border: "#059669" },
  standard: { text: "#0369a1", bg: "#f0f9ff", border: "#0369a1" },
  graded: { text: "#d97706", bg: "#fffbeb", border: "#d97706" },
  modified: { text: "#dc2626", bg: "#fef2f2", border: "#dc2626" },
  level: { text: "#059669", bg: "#ecfdf5", border: "#059669" },
};

function getTierStyle(tier: string) {
  const key = tier.toLowerCase().replace(/\s+/g, "");
  for (const [k, v] of Object.entries(TIER_COLORS)) {
    if (key.includes(k)) return v;
  }
  return { text: "#6b7280", bg: "#f9fafb", border: "#6b7280" };
}

export default function CarrierComparisonCard({ data }: { data: CarrierComparisonCardData }) {
  if (!data.carriers || data.carriers.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ boxShadow: S }}>
      <div className="bg-white px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-3.5 w-3.5 text-primary" />
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em]">Carrier Eligibility</p>
          <span className="text-[10px] text-gray-500 ml-auto">{data.carriers.length} carriers</span>
        </div>

        <div className="space-y-1.5">
          {data.carriers.map((carrier, i) => {
            const tierStyle = getTierStyle(carrier.tier);
            return (
              <div key={i} className="flex items-center gap-2.5 rounded-md p-2 hover:bg-gray-50 transition-colors" style={{ borderLeft: `3px solid ${tierStyle.border}` }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-900">{carrier.name}</span>
                    <span className="text-[7px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: tierStyle.text, backgroundColor: tierStyle.bg }}>
                      {carrier.tier}
                    </span>
                  </div>
                  {carrier.reason && (
                    <p className="text-[10px] text-gray-500 mt-0.5 truncate">{carrier.reason}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {carrier.monthlyPremium && (
                    <span className="text-xs font-mono font-bold text-emerald-600" style={{ fontFeatureSettings: '"tnum"' }}>
                      ${carrier.monthlyPremium.toFixed(2)}
                    </span>
                  )}
                  {carrier.amBestRating && (
                    <div className="flex items-center gap-0.5">
                      <Star className="h-2.5 w-2.5 text-amber-500" />
                      <span className="text-[8px] font-bold text-gray-500">{carrier.amBestRating}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
